import { Offcanvas } from 'bootstrap';
import * as packageJson from 'package.json';
import { combineLatest, takeUntil } from 'rxjs';
import { AppRoutes } from 'src/app/app.routes';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuthService } from '@auth/services/auth/auth.service';
import { INavigationItem } from '@layout/models/navigation';
import { OrganisationSelectorComponent } from '@organisations/components/organisation-selector/organisation-selector.component';
import { OrganisationUserAccessLevel } from '@organisations/models/organisation-user';
import { OrganisationRoutes } from '@organisations/organisation.routes';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { OrganisationUserService } from '@organisations/services/organisation-user/organisation-user.service';
import { OrganisationSettingsRoutes, SettingsRoutes, UserSettingsRoutes } from '@settings/settings.routes';

import { MenuNavItemComponent } from '../menu-nav-item/menu-nav-item.component';

@Component({
	selector: 'app-menu',
	imports: [CommonModule, OrganisationSelectorComponent, MenuNavItemComponent, RouterLink],
	templateUrl: './menu.component.html',
	styleUrl: './menu.component.scss',
})
export class MenuComponent implements OnInit, OnDestroy {
	@Input() isCollapsed = false;
	destroyed$ = new EventEmitter<void>();
	AppRoutes = AppRoutes;
	SettingsRoutes = SettingsRoutes;

	authService = inject(AuthService);
	private organisationSelectedService = inject(OrganisationSelectedService);
	private organisationUserService = inject(OrganisationUserService);

	private organisationEnabled = false;
	private clientsEnabled = false;
	private leadsEnabled = false;
	private scheduleEnabled = false;
	private quotesInvoicesEnabled = false;
	private inventoryEnabled = false;

	navigationItems: Array<INavigationItem> = [];
	version: string;

	organisationInvitationCount: number = 0;
	selectedOrganisationId: string | undefined | null = null;

	ngOnInit(): void {
		combineLatest([this.organisationSelectedService.selectedOrganisation, this.organisationUserService.organisationInvitations()])
			.pipe(takeUntil(this.destroyed$))
			.subscribe(([organisationUser, organisationInvitations]) => {
				this.organisationInvitationCount = organisationInvitations.length;
				this.organisationUserService.userHasOrganisationInvitations$.next(!!organisationInvitations.length);
				this.selectedOrganisationId = organisationUser?.organisationUser.organisation.id;

				const accessLevel = organisationUser?.organisationUser.accessLevel;
				const isOwner = accessLevel
					? [OrganisationUserAccessLevel.OWNER, OrganisationUserAccessLevel.ADMIN].includes(accessLevel)
					: false;

				this.organisationEnabled = isOwner;
				this.clientsEnabled = isOwner;
				this.quotesInvoicesEnabled = isOwner;
				this.inventoryEnabled = isOwner;
				this.leadsEnabled = isOwner;
				this.scheduleEnabled = !!organisationUser;
				this.setNavigationItems();
			});
		this.version = packageJson.version;
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	handleSignOut(): void {
		this.authService.signOut();
	}

	setNavigationItems(): void {
		this.navigationItems = [
			{
				icon: 'bi-house',
				label: 'Dashboard',
				route: [AppRoutes.DASHBOARD],
				disabled: false,
				visible: true,
			},
			{
				icon: 'bi-bucket',
				label: 'Leads',
				route: [AppRoutes.CLIENT_LEADS_DASHBOARD],
				disabled: !this.leadsEnabled,
				visible: this.leadsEnabled,
			},
			{
				icon: 'bi-calendar2-plus',
				label: 'Schedule',
				route: [AppRoutes.SCHEDULE],
				disabled: !this.scheduleEnabled,
				visible: this.scheduleEnabled,
			},
			{
				icon: 'bi-people',
				label: 'Clients',
				route: [AppRoutes.CLIENTS],
				disabled: !this.clientsEnabled,
				visible: this.clientsEnabled,
			},
			{
				icon: 'bi-bag',
				label: 'Inventory',
				route: [AppRoutes.INVENTORY],
				disabled: !this.inventoryEnabled,
				visible: this.inventoryEnabled,
			},
			// {
			// 	icon: 'bi-file-earmark-bar-graph',
			// 	label: 'Quotes & Invoices',
			// 	externalRoute: `${environment.invoicesQuotesUrl}?org_id=${this.selectedOrganisationId}`,
			// 	disabled: !this.quotesInvoicesEnabled,
			// 	visible: this.quotesInvoicesEnabled,
			// },
			{
				icon: 'bi-diagram-2',
				label: 'Organisation',
				route: [AppRoutes.ORGANISATION],
				disabled: !this.organisationEnabled,
				visible: this.organisationEnabled,
				children: [
					{
						icon: 'bi-briefcase',
						label: 'Details',
						route: [AppRoutes.ORGANISATION, OrganisationRoutes.DETAILS],
						disabled: !this.organisationEnabled,
						visible: this.organisationEnabled,
					},
					{
						icon: 'bi-person-fill-add',
						label: 'Users',
						route: [AppRoutes.ORGANISATION, OrganisationRoutes.USERS],
						disabled: !this.organisationEnabled,
						visible: this.organisationEnabled,
					},
					{
						icon: 'bi-receipt',
						label: 'Billing',
						route: [AppRoutes.ORGANISATION, OrganisationRoutes.BILLING],
						disabled: !this.organisationEnabled,
						visible: this.organisationEnabled,
					},
				],
			},
			{
				icon: 'bi-gear',
				label: 'Settings',
				route: [AppRoutes.SETTINGS],
				disabled: false,
				visible: true,
				children: [
					{
						icon: 'bi-diagram-2',
						label: 'Organisation',
						route: [AppRoutes.SETTINGS, SettingsRoutes.ORGANISATION],
						disabled: !this.organisationEnabled,
						visible: this.organisationEnabled,
						children: [
							{
								icon: 'bi-clock',
								label: 'Business Hours',
								route: [AppRoutes.SETTINGS, SettingsRoutes.ORGANISATION, OrganisationSettingsRoutes.BUSINESS_HOURS],
								disabled: !this.organisationEnabled,
								visible: this.organisationEnabled,
							},
							{
								icon: 'bi-backpack',
								label: 'Service Types',
								route: [AppRoutes.SETTINGS, SettingsRoutes.ORGANISATION, OrganisationSettingsRoutes.SERVICE_TYPES],
								disabled: !this.organisationEnabled,
								visible: this.organisationEnabled,
							},
							{
								icon: 'bi-ui-checks',
								label: 'Assessment Form',
								route: [AppRoutes.SETTINGS, SettingsRoutes.ORGANISATION, OrganisationSettingsRoutes.ASSESSMENT_FORM],
								disabled: !this.organisationEnabled,
								visible: this.organisationEnabled,
							},
							{
								icon: 'bi-link',
								label: 'Leads Link',
								route: [AppRoutes.SETTINGS, SettingsRoutes.ORGANISATION, OrganisationSettingsRoutes.LEADS_LINK],
								disabled: !this.organisationEnabled,
								visible: this.organisationEnabled,
							},
						],
					},
					{
						icon: 'bi-person-gear',
						label: 'User',
						route: [AppRoutes.SETTINGS, SettingsRoutes.USER],
						disabled: false,
						visible: true,
						children: [
							{
								icon: 'bi-person-circle',
								label: 'Profile',
								route: [AppRoutes.SETTINGS, SettingsRoutes.USER, UserSettingsRoutes.PROFILE],
								disabled: false,
								visible: true,
							},
							{
								icon: 'bi-bell',
								label: 'Notifications',
								route: [AppRoutes.SETTINGS, SettingsRoutes.USER, UserSettingsRoutes.NOTIFICATIONS],
								disabled: false,
								visible: true,
							},
							{
								icon: 'bi-mailbox-flag',
								label: 'Invitations',
								route: [AppRoutes.SETTINGS, SettingsRoutes.USER, UserSettingsRoutes.ORGANISATION_INVITES],
								disabled: false,
								visible: true,
								notificationCount: this.organisationInvitationCount,
							},
						],
					},
				],
			},
		];

		this.aggregateNotificationCounts(this.navigationItems);
	}

	private aggregateNotificationCounts(items: INavigationItem[]): number {
		let total = 0;
		for (const item of items) {
			if (item.children?.length) {
				item.notificationCount = this.aggregateNotificationCounts(item.children);
			}
			total += item.notificationCount ?? 0;
		}
		return total;
	}

	dismissOffcanvas(item?: INavigationItem): void {
		if (!item) {
			const mobileOffcanvas = Offcanvas.getInstance('#mobileNavbar');
			mobileOffcanvas?.hide();
			return;
		}
		if (!item.children) {
			const mobileOffcanvas = Offcanvas.getInstance('#mobileNavbar');
			mobileOffcanvas?.hide();
		}
	}
}
